import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { Button, Theme } from '@carbon/react';
import { Close, Search as SearchIcon } from '@carbon/icons-react';
import axiosInstance from '../../utils/axiosInstance';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { useTheme } from '../../contexts/ThemeContext';
import { pluralForm } from '../workday/workDayUtils';
import './AssignTaskPanel.css';

// Izvlači niz iz različitih oblika odgovora (isti tolerantni pristup kao drugde)
const extractArray = (res) => {
  const body = res.data;
  if (Array.isArray(body)) return body;
  if (body && Array.isArray(body.data)) return body.data;
  if (body && Array.isArray(body.value)) return body.value;
  if (body && Array.isArray(body.$values)) return body.$values;
  if (body && Array.isArray(body.items)) return body.items;
  if (body && body.data && Array.isArray(body.data.$values)) return body.data.$values;
  if (body && body.data && Array.isArray(body.data.items)) return body.data.items;
  return [];
};

const byName = (a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'sr');

// Zaposleni koji su danas u smeni - iz GET /api/Shift/current-shifts
// (odgovor: { areas: [{ area, employees: [{ employeeId, employeeName, employeeTypeName }] }] }).
// Isti zaposleni se može pojaviti u više oblasti, pa se lista dedup-uje.
const fetchOnShiftEmployees = async () => {
  const response = await axiosInstance.get('/api/Shift/current-shifts');
  const body = response.data && response.data.data ? response.data.data : response.data;
  const areas = Array.isArray(body?.areas) ? body.areas : [];
  const seen = new Set();
  const list = [];
  areas.forEach((area) => {
    const emps = Array.isArray(area?.employees) ? area.employees : [];
    emps.forEach((emp) => {
      const id = emp?.employeeId ?? emp?.id;
      if (!id || seen.has(id)) return;
      seen.add(id);
      list.push({
        id,
        name: emp?.employeeName || emp?.name || '',
        role: emp?.employeeTypeName || emp?.employeeType || '',
      });
    });
  });
  return list.sort(byName);
};

// Svi zaposleni - GET /api/Employee (server-side lista)
const fetchAllEmployees = async () => {
  const params = new URLSearchParams();
  params.append('pageNumber', '1');
  params.append('pageSize', '200');
  const response = await axiosInstance.get(`/api/Employee?${params.toString()}`);
  return extractArray(response)
    .map((e) => ({
      id: e?.id ?? e?.employeeId,
      name: e?.fullName || `${e?.firstName || ''} ${e?.lastName || ''}`.trim(),
      role: e?.employeeTypeName || e?.employeeType || '',
    }))
    .filter((e) => e.id)
    .sort(byName);
};

const initialsOf = (name) => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return parts.slice(0, 2).map((p) => p[0]).join('').toUpperCase();
};


/**
 * AssignTaskPanel - desni sidebar za dodelu izabranih zadataka zaposlenom.
 * Prvo prikazuje zaposlene koji su danas u smeni (GET /api/Shift/current-shifts),
 * a dugmetom "Svi zaposleni" se učitava kompletna lista (GET /api/Employee).
 * Klik na zaposlenog dodeljuje izabrane zadatke (POST /api/Task/assign).
 * Uvek je montiran (radi slide animacije), a `.open` ga uvodi u vidno polje.
 */
const AssignTaskPanel = ({ open, taskIds = [], onClose, onAssigned }) => {
  const { theme } = useTheme();
  // 'shift' = zaposleni u smeni danas, 'all' = svi zaposleni
  const [scope, setScope] = useState('shift');
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [assigning, setAssigning] = useState(false);

  // Pri svakom otvaranju: kreni od zaposlenih u smeni i očisti pretragu
  useEffect(() => {
    if (open) {
      setScope('shift');
      setSearch('');
      setError(null);
    }
  }, [open]);

  // Učitaj listu prema izabranom obuhvatu ('shift' ili 'all')
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const list = scope === 'shift' ? await fetchOnShiftEmployees() : await fetchAllEmployees();
        if (!cancelled) setEmployees(list);
      } catch (err) {
        console.error('Greška pri učitavanju zaposlenih:', err);
        if (!cancelled) {
          setEmployees([]);
          setError('Nije moguće učitati zaposlene. Pokušajte ponovo.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [open, scope]);

  // Pretraga (klijentski filter) po imenu i zanimanju
  const visibleEmployees = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return employees;
    return employees.filter((e) => `${e.name} ${e.role}`.toLowerCase().includes(query));
  }, [employees, search]);

  const count = taskIds.length;

  const handleAssign = async (employee) => {
    if (count === 0 || assigning) return;
    setAssigning(true);
    setError(null);
    try {
      await axiosInstance.post('/api/Task/assign', {
        taskIds,
        employeeId: employee.id,
      });
      if (onAssigned) onAssigned(employee);
    } catch (err) {
      console.error('Greška pri dodeli zadataka:', err);
      setError(
        getApiErrorMessage(err, 'Nije moguće dodeliti zadatke. Pokušajte ponovo.')
      );
    } finally {
      setAssigning(false);
    }
  };

  return (
    <>
      {ReactDOM.createPortal(
        /* Panel je portal-ovan na document.body (zbog fiksne pozicije ispod
           transform:scale zaglavlja), pa se nalazi van Carbon <Theme> omotača
           iz Shell-a. Zato ga ovde ponovo umotavamo u <Theme> da bi nasledio
           aktivnu temu umesto da padne na svetlu (white). */
        <Theme theme={theme}>
          <div
            className={`adash-assign-panel ${open ? 'open' : ''}`}
            aria-hidden={!open}
          >
            <div className="adash-assign-panel__header">
              <div className="adash-assign-panel__head-text">
                <h3 className="adash-assign-panel__title">Dodeli</h3>
                {count > 0 && (
                  <span className="adash-assign-panel__subtitle">
                    {count === 1
                      ? '1 zadatak'
                      : `${count} ${pluralForm(count, 'zadatak', 'zadatka', 'zadataka')}`}
                  </span>
                )}
              </div>
              <Button
                kind="ghost"
                size="sm"
                renderIcon={Close}
                iconDescription="Zatvori"
                hasIconOnly
                onClick={onClose}
              />
            </div>

            <div className="adash-assign-panel__content">
              {/* Obuhvat: zaposleni u smeni danas (podrazumevano) ili svi zaposleni */}
              <div
                className="adash-assign-panel__scope"
                role="tablist"
                aria-label="Izvor liste zaposlenih"
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={scope === 'shift'}
                  className={`adash-assign-panel__scope-btn ${scope === 'shift' ? 'is-active' : ''}`}
                  onClick={() => setScope('shift')}
                >
                  U smeni danas
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={scope === 'all'}
                  className={`adash-assign-panel__scope-btn ${scope === 'all' ? 'is-active' : ''}`}
                  onClick={() => setScope('all')}
                >
                  Svi zaposleni
                </button>
              </div>

              {/* Pretraga zaposlenih po imenu ili zanimanju */}
              <div className="adash-assign-panel__search">
                <SearchIcon size={16} className="adash-assign-panel__search-icon" aria-hidden="true" />
                <input
                  type="search"
                  className="adash-assign-panel__search-input"
                  placeholder="Pretraži zaposlene..."
                  aria-label="Pretraga zaposlenih"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  autoComplete="off"
                  maxLength={100}
                />
              </div>

              <div className="adash-assign-panel__list">
                {loading ? (
                  <div className="adash-assign-panel__skeleton">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <span className="adash-assign-panel__skeleton-row" key={i} />
                    ))}
                  </div>
                ) : error ? (
                  <div className="adash-assign-panel__empty">{error}</div>
                ) : visibleEmployees.length === 0 ? (
                  <div className="adash-assign-panel__empty">
                    {scope === 'shift'
                      ? 'Trenutno nema zaposlenih u smeni'
                      : 'Nema zaposlenih'}
                  </div>
                ) : (
                  visibleEmployees.map((emp) => (
                    <button
                      key={emp.id}
                      type="button"
                      className="adash-assign-panel__employee"
                      onClick={() => handleAssign(emp)}
                      disabled={assigning}
                      title={`Dodeli zadatke: ${emp.name}`}
                    >
                      <span className="adash-assign-panel__avatar" aria-hidden="true">
                        {initialsOf(emp.name)}
                      </span>
                      <span className="adash-assign-panel__emp-text">
                        <span className="adash-assign-panel__emp-name">{emp.name}</span>
                        {emp.role && (
                          <span className="adash-assign-panel__emp-role">{emp.role}</span>
                        )}
                      </span>
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        </Theme>,
        document.body
      )}

      {open &&
        ReactDOM.createPortal(
          <div className="adash-assign-panel__overlay" onClick={onClose} />,
          document.body
        )}
    </>
  );
};

export default AssignTaskPanel;
